import React, { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import moment from 'moment';
import Pagination from '../../pagination';
import 'react-lazy-load-image-component/src/effects/blur.css';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { setSelectedSection } from '../../../../redux/actions/sideBarAction';
import TableLoadingSkeleton from '../../../Utils/LoadingAnimations/TableLoadingSkeleton';
import { useTypedSelector } from '../../../../hooks/useTypeSelector';
import LazyImage from '../Components/imageComponent';
import { globalEnv } from '../../../../config/baseConfig';
import { HeaderItem, sortingOrder } from '../Components/common';
import { DataNotFoundAnimation } from '../../../Utils/ErrorAnimations/dataNotFoundAnim';
import { axiosInstance } from '../../../../config/axiosInstance';
import ParentModal from '../../../Utils/Components/parentModal';
import { UninstallReasonsModalContent } from './uninstallReasonsModal';
import { setSearchPlaceholderAction } from '../../../../redux/actions/searchBarAction';

export default function UninstallTable({
	extensionId
}: {
	extensionId?: string;
}) {
	const queryClient = useQueryClient();
	const [page, setPage] = useState(1);
	const [sortOrder, setSortOrder] = useState(sortingOrder.DESCENDING);
	const [selectedHeader, setSelectedHeader] = useState('createdAt');
	const [usersCount, setUsersCount] = useState(0);

	const [isShowingUninstallReasonsModal, setIsShowingUninstallReasonsModal] =
		useState(false);
	const [selectedUninstallReason, setSelectedUninstallReason] = useState<
		{ reasons: string[]; detailedReasons?: string } | undefined
	>(undefined);

	const { ACCESS_TOKEN } = useTypedSelector((state) => state.auth);
	const { searchQuery }: { searchQuery: string } = useTypedSelector(
		(state) => state.searchbar
	);

	const [dimensions, setDimensions] = useState({
		width: window.innerWidth,
		height: window.innerHeight
	});

	const dispatch = useDispatch();

	useEffect(() => {
		dispatch(setSelectedSection('User Documents'));
		dispatch(setSearchPlaceholderAction('Search for Users'));
	}, []);

	useEffect(() => {
		function handleResize() {
			setDimensions({
				width: window.innerWidth,
				height: window.innerHeight
			});
		}
		window.addEventListener('resize', handleResize);
		return () => {
			window.removeEventListener('resize', handleResize);
		};
	}, []);

	const { data, isError, isFetching, isLoading } = useQuery(
		[page, selectedHeader, sortOrder, searchQuery],
		() => fetchUserUninstalls(page, ACCESS_TOKEN),
		{
			cacheTime: 0,
			keepPreviousData: false,
			staleTime: 5000,
			refetchOnWindowFocus: false
		}
	);

	// Prefetch the next page!
	useEffect(() => {
		if (data?.hasMore) {
			queryClient.prefetchQuery([page + 1], () =>
				fetchUserUninstalls(page + 1, ACCESS_TOKEN)
			);
		}
	}, [data, page, queryClient]);

	useEffect(() => {
		if (data != null) {
			if (data.uninstalls != null) setUsersCount(data.meta.total);
		}
	}, [data]);

	useEffect(() => {
		const intervalId = setInterval(() => {
			window.location.reload();
		}, 1000 * 60 * 20);
		return () => clearInterval(intervalId);
	}, []);

	async function fetchUserUninstalls(page: number, token: string) {
		const config = {
			headers: {
				Authorization: 'Bearer ' + token
			}
		};

		let extensionsUrl = '';
		if (extensionId != null) extensionsUrl = `extension=${extensionId}&`;

		const { data } = await axiosInstance.get(
			globalEnv.BASE_URL +
				`/admin/user/uninstall?${extensionsUrl}page=${page}&sortby=${selectedHeader}&order=${sortOrder}${
					searchQuery !== '' ? '&filter=' + searchQuery : ''
				}`,
			config
		);
		return data;
	}

	const TableHeaders = () => {
		return (
			<tr>
				<HeaderItem
					name="Possible Users"
					slugName="users"
					isCenter={false}
					selectedHeader={selectedHeader}
					setSelectedHeader={setSelectedHeader}
					sortOrder={sortOrder}
					setSortOrder={setSortOrder}
				/>

				<HeaderItem
					name="device id"
					slugName="deviceId"
					isCenter={false}
					selectedHeader={selectedHeader}
					setSelectedHeader={setSelectedHeader}
					sortOrder={sortOrder}
					setSortOrder={setSortOrder}
				/>
				<HeaderItem
					name="Reasons"
					slugName="uninstallReasons"
					isCenter={true}
					selectedHeader={selectedHeader}
					setSelectedHeader={setSelectedHeader}
					sortOrder={sortOrder}
					setSortOrder={setSortOrder}
				/>
				<HeaderItem
					name="Uninstall Date"
					slugName="createdAt"
					isCenter={true}
					selectedHeader={selectedHeader}
					setSelectedHeader={setSelectedHeader}
					sortOrder={sortOrder}
					setSortOrder={setSortOrder}
				/>

				<HeaderItem
					name=""
					slugName=""
					isCenter={true}
					selectedHeader={selectedHeader}
					setSelectedHeader={setSelectedHeader}
					sortOrder={sortOrder}
					setSortOrder={setSortOrder}
				/>

				<th className="absolute p-2 whitespace-nowrap right-0">
					<div className="font-semibold text-left">
						{`Count: ${usersCount}`}
					</div>
				</th>
			</tr>
		);
	};

	return (
		<section>
			{selectedUninstallReason != null && (
				<ParentModal
					visible={isShowingUninstallReasonsModal}
					setModalOpen={setIsShowingUninstallReasonsModal}
					isChanged={false}
					setIsChanged={() => {
						return;
					}}
					isWarnModalOpen={false}
					setIsWarnModalOpen={() => {
						return;
					}}
				>
					<UninstallReasonsModalContent
						reasonsList={selectedUninstallReason.reasons}
						detailedReasons={selectedUninstallReason.detailedReasons}
						extensionId={extensionId}
					/>
				</ParentModal>
			)}
			<div className="p-3">
				<div>
					<div className="overflow-x-auto ">
						<table className="table-auto w-full">
							{isFetching || isLoading || isError ? (
								<></>
							) : data.uninstalls != null ? (
								<thead className="text-xs font-bold uppercase dark:text-primary-text-dark text-primary-text-light">
									<TableHeaders />
								</thead>
							) : (
								<></>
							)}
							{isFetching || isLoading || isError ? (
								<></>
							) : (
								<tbody className="text-sm divide-y divide-gray-300 dark:divide-gray-600 transition-all duration-500">
									{data.uninstalls != null &&
										data.uninstalls.length > 0 &&
										data.uninstalls.map((item: any) => (
											<TableData
												key={item._id}
												id={item._id}
												users={item.users}
												deviceId={item.deviceId}
												reasons={item.uninstallReasons}
												detailedReasons={item.uninstallReasonDetails}
												createdAt={item.createdAt}
												setIsShowingUninstallReasonsModal={
													setIsShowingUninstallReasonsModal
												}
												setSelectedUninstallReason={setSelectedUninstallReason}
											/>
										))}
								</tbody>
							)}
						</table>
					</div>
				</div>
				{isFetching || isLoading ? (
					<div>
						<TableLoadingSkeleton />
						<TableLoadingSkeleton />
						<TableLoadingSkeleton />
						<TableLoadingSkeleton />
						<TableLoadingSkeleton />
						<TableLoadingSkeleton />
						<TableLoadingSkeleton />
						<TableLoadingSkeleton />
						<TableLoadingSkeleton />
						<TableLoadingSkeleton />
						<TableLoadingSkeleton />
					</div>
				) : isError ? (
					<div className="h-full ml-0 ">
						<div className=" flex flex-col mx-auto my-auto text-center align-middle overflow-clip h-full ">
							<div className="mx-auto my-auto self-center">
								<div className="min-h-16">
									<DataNotFoundAnimation height={dimensions.height / 1.5} />
								</div>
								<h3 className="lg:text-2xl text-xl font-bold dark:text-gray-200 text-gray-700 -mt-8">
									{"Couldn't find the data you're looking for!"}
								</h3>
							</div>
						</div>
					</div>
				) : data.uninstalls != null ? (
					<Pagination
						itemsPerPage={data.meta.limit}
						totalCount={data.meta.total}
						totalPages={data.meta.pages}
						currentPage={page}
						setPage={setPage}
					/>
				) : (
					<div className="h-full ml-0 ">
						<div className=" flex flex-col mx-auto my-auto text-center align-middle overflow-clip h-full ">
							<div className="mx-auto my-auto self-center">
								<div className="min-h-16">
									<DataNotFoundAnimation height={dimensions.height / 1.5} />
								</div>
								<h3 className="lg:text-2xl text-xl font-bold dark:text-gray-200 text-gray-700 -mt-8">
									{"Couldn't find the data you're looking for!"}
								</h3>
							</div>
						</div>
					</div>
				)}
			</div>

			{/* <ReactQueryDevtools initialIsOpen /> */}
		</section>
	);
}

type tableDateType = {
	id: string;
	users: any[];
	deviceId: string;
	reasons: string[];
	detailedReasons?: string;
	createdAt: string;
	setIsShowingUninstallReasonsModal: (
		isShowingUninstallReasonsModal: boolean
	) => void;
	setSelectedUninstallReason: (selectedUninstallReason: {
		reasons: string[];
		detailedReasons?: string;
	}) => void;
};

const TableData = ({
	id,
	users,
	deviceId,
	reasons,
	detailedReasons,
	createdAt,
	setIsShowingUninstallReasonsModal,
	setSelectedUninstallReason
}: tableDateType) => {
	const navigate = useNavigate();
	return (
		<tr>
			<td
				className="w-1/12 p-2 break-all"
				onClick={() => {
					if (users.length > 0) navigate(`../deviceid-user-list/${deviceId}`);
				}}
			>
				<div
					className={`flex items-center ${
						users.length > 0 && 'cursor-pointer'
					}`}
					onClick={() => {
						navigate(`../user/${id}`);
					}}
				>
					<div
						className={`h-10 w-24 flex justify-center items-center flex-shrink-0 mr-2 sm:mr-3 $`}
					>
						{users.length > 0 ? (
							users.map((user) => (
								<LazyImage
									photo={
										user.photo != null
											? user.photo
											: `https://api.dicebear.com/7.x/bottts/svg?seed=${user.username}`
									}
								/>
							))
						) : (
							<h5> No users found</h5>
						)}
					</div>
				</div>
			</td>
			<td
				className={` w-3/12 md:w-3/12 p-2 break-all ${
					users.length > 0 && 'cursor-pointer'
				}`}
				onClick={() => {
					if (users.length > 0) navigate(`../deviceid-user-list/${deviceId}`);
				}}
			>
				<div className="font-medium dark:text-primary-text-dark text-primary-text-light">
					{deviceId.replace(
						/(^[a-zA-Z0-9]{6})(.*)([a-zA-Z0-9]{8}$)/,
						(_, firstThree, middle, lastThree) =>
							firstThree + '.'.repeat(middle.length) + lastThree
					)}
				</div>
			</td>
			<td
				className={`w-3/12 md:w-5/12 p-2 break-all ${
					(reasons.length > 1 || detailedReasons != null) && 'cursor-pointer'
				}`}
				onClick={() => {
					setSelectedUninstallReason({
						reasons: reasons,
						detailedReasons: detailedReasons
					});
					setIsShowingUninstallReasonsModal(true);
				}}
			>
				<div className="text-left dark:text-secondary-text-dark text-secondary-text-light font-medium">
					{reasons.length > 1
						? `${reasons[0]}, ${reasons[1]}, ...`
						: reasons[0]}
				</div>
			</td>
			<td className="md:w-2/12 p-2 whitespace-nowrap">
				<div className="text-center font-medium dark:text-secondary-text-dark text-secondary-text-light">
					{moment(createdAt).format('MM/DD/YYYY - HH:mm')}
				</div>
			</td>
		</tr>
	);
};
